// A co-op statement's review (plan/supplier-cashbooks.md §9): where the import routed each sub-account (which
// cashbook, made now or matched, how many lines; the sub-accounts it skipped and why) beside the reconcile-to-source
// tie-out (§7) — each cashbook's opening + lines against the closing the statement prints, each document's lines
// against its printed total and VAT, the statement's own VAT analysis, the diesel litres — and what holds it. A held
// statement posts nothing until it ties. Each document links to the page it is printed on (the statement's own page
// image, opened on top). GET /statement-sets/{id} runs the tie-out again, so the review is always the books as they are.
// The co-op's FIRST statement also says where the books may already hold its opening (on the co-op as a supplier, or an
// account kept for it) and moves it in once, so the balance is in the books once (GET/POST/DELETE
// /coops/{id}/opening, the API's coop_opening_service; 2026-10-25) — the farmer's own funds at the co-op apart, on the
// first statement that shows them (?part=funds; 2026-10-27, plan §12.5). A LATER statement says how each sub-account carries
// on from the co-op's statement before it: a balance the co-op moved between its sub-accounts (a season change —
// booked, waiting, or held by a locked year), a statement missing in between, or openings no move explains (the
// tie-out's continuity, the API's coop_rollover_service; 2026-10-25). Openings no move explains, on a statement that is
// right (the co-op paid part of it from the farmer's own funds with them that the statement does not show — funds it
// shows are a cashbook of their own since 2026-10-27, and the API books such a payment as a move — or charged or
// credited something with no line, or a sub-account first shows a balance it carried before), are booked to the
// account the difference belongs to, and the booking can be undone (GET/POST/DELETE
// /statement-sets/{id}/difference; 2026-10-26). Every statement lists the purchase documents on the co-op as a supplier
// dated in its period: one whose purchase is on the statement is in the books twice (GET
// /statement-sets/{id}/supplier-documents; Fable's final check, finding 5; 2026-10-26). A held statement can be read again
// from its PDF (POST /statement-sets/{id}/read-again, the API's bank_import_service.read_again; 2026-10-26): the page
// follows the read as an import (onReadAgain) and opens the review again when it is done; a read that fails or is
// cancelled is said under its button (readAgainEnded); where it cannot be read again the review says why
// (read_again.why).
//   const review = coopReview(companyId, { onOpenCashbook: id => …, onChecked: diff => …,
//                                          onReadAgain: (jobId, setId, label) => … });
//   review.open(setId);
//   review.readAgainEnded(setId, why);               // the read again the page followed failed, or was cancelled
//   ${review.view}                                   // once, in a template built once
import { html, signal, navigate } from '../shared/t4.js';
import { api } from '../shared/api.js';
import { t } from '../shared/i18n.js';
import { searchSelect } from '../shared/searchselect.js';
import { statementViewer } from '../shared/statementview.js';
import { fmtMoney, baseCurrencySymbol, formatDate } from './bank_helpers.js';
import { setStatus, reviewDocuments, pageIndex, heldWhy, heldReasons } from './coop_helpers.js';
import { DOC_TYPES, statusLabel, statusClass } from './documents.js';

const DOCS_SHOWN = 12;           // the documents that tie past these wait behind "Show all"
const BASIS = { standard: 'Standard rated', zero: 'Zero rated', exempt: 'Exempt' };   // the statement's VAT analysis rows
// a document type's screen (its /co/{id}/{screen} segment), as the document editor finds it
const KEY_FOR_TYPE = Object.entries(DOC_TYPES).reduce((m, [k, v]) => { if(v.type && v.type !== 'ALL') m[v.type] = k; return m; }, {});

export function coopReview(companyId, { onOpenCashbook, onChecked, onReadAgain, dateFormat } = {}){
  const P = p => '/companies/' + companyId + p;
  // {status:'loading'|'error'|'ready', setId, diff, error, allDocs, opening, openBusy, openErr, openMsg, confirmUndo,
  //  fundsOpening, fundsBusy, fundsErr, fundsMsg, confirmFundsUndo,
  //  difference, diffBusy, diffErr, diffMsg, confirmDiffUndo, supplierDocs, raBusy, raErr, raJob}
  const state = signal(null);
  const stmt = statementViewer(companyId);
  let seq = 0;
  // the co-op's opening is moved in by part (2026-10-27, plan §12.5): 'coop' — what the farmer owes the co-op, as before
  // — and 'funds' — the farmer's own funds at it, an asset the books keep elsewhere (?part=funds): each its own state keys
  const PARTS = {
    coop: { opening: 'opening', busy: 'openBusy', err: 'openErr', msg: 'openMsg', confirm: 'confirmUndo', q: '' },
    funds: { opening: 'fundsOpening', busy: 'fundsBusy', err: 'fundsErr', msg: 'fundsMsg', confirm: 'confirmFundsUndo',
             q: '?part=funds' },
  };
  // the place chosen in each part's <select> ('contact:12' | 'account:34') — no redraw
  const picks = { coop: '', funds: '' };
  const openSeqs = { coop: 0, funds: 0 };   // the latest read of a part's opening wins: a check, a move and an undo can overlap
  let diffSeq = 0;              // the latest read of the difference wins, likewise
  let docsSeq = 0;              // the latest read of the documents on the co-op as a supplier wins, likewise
  // the account a difference is booked to: built once and moved into every draw of the panel (a node keeps what is typed
  // in it), so choosing never redraws the review
  const diffPick = searchSelect({
    placeholder: t('coop.diff_pick', '— Choose the account, e.g. 5100 Equity - Opening Balance —'),
    ariaLabel: t('coop.diff_where', 'The account the difference belongs to'), className: 'form-control cb-in coop-diff-pick',
    emptyText: t('coop.diff_none', 'No account matches'),
  });

  // the difference no move explains, on a later statement (GET …/difference): where it may be booked, why it cannot be
  // yet, or where it is booked. Read only when the continuity has one (unexplained or explained) or one is booked for the
  // statement (undo); null otherwise, or when it cannot be read. A chosen account no longer offered is dropped.
  async function readDifference(setId, diff){
    const c = (diff || {}).continuity;
    const mine = ++diffSeq;
    const wanted = !!c && (c.verdict === 'unexplained' || c.verdict === 'explained' || !!c.undo);
    const d = wanted ? await api.get(P(`/statement-sets/${setId}/difference`)).catch(() => null) : null;
    const s = state.value;
    if(mine !== diffSeq || !s || s.setId !== setId) return;
    const accounts = (d && d.accounts) || [];
    diffPick.setOptions(accounts.map(a => ({ value: String(a.id), label: `${a.code} ${a.name}` })));
    diffPick.set(accounts.some(a => String(a.id) === diffPick.get()) ? diffPick.get() : '');
    patch({ difference: d });
  }

  // the purchase documents on the co-op as a supplier dated in the statement's period (GET …/supplier-documents): one
  // whose purchase is on the statement is in the books twice. null when they cannot be read.
  async function readSupplierDocs(setId){
    const mine = ++docsSeq;
    const d = await api.get(P(`/statement-sets/${setId}/supplier-documents`)).catch(() => null);
    const s = state.value;
    if(mine !== docsSeq || !s || s.setId !== setId) return;
    patch({ supplierDocs: d });
  }

  // the co-op's opening (of a part): where the books may already hold it, or the move made. undefined when a later read,
  // or another statement's review, has taken its place; null when it cannot be read. A place chosen that is no longer
  // offered is dropped.
  async function readOpening(setId, groupId, part = 'coop'){
    const mine = ++openSeqs[part];
    const opening = groupId ? await api.get(P(`/coops/${groupId}/opening${PARTS[part].q}`)).catch(() => null) : null;
    const s = state.value;
    if(mine !== openSeqs[part] || !s || s.setId !== setId) return undefined;
    if(picks[part] && !((opening && opening.candidates) || []).some(c => c.kind + ':' + c.id === picks[part])) picks[part] = '';
    return opening;
  }

  // also: what the review says from the start (a read again that ended: readAgainEnded) — a check again forgets it
  async function open(setId, also = {}){
    if(!setId) return;
    const my = ++seq;
    const keep = state.value && state.value.setId === setId ? state.value : null;
    if(!keep){ picks.coop = ''; picks.funds = ''; diffPick.set(''); }
    // a check again keeps what the last one said until its own read is in (a booking under way stays busy)
    const kept = keep ? { opening: keep.opening, fundsOpening: keep.fundsOpening, difference: keep.difference,
      diffBusy: keep.diffBusy, supplierDocs: keep.supplierDocs } : {};
    state.value = { status: 'loading', setId, diff: keep && keep.diff, allDocs: !!(keep && keep.allDocs), ...kept, ...also };
    let diff;
    try { diff = await api.get(P(`/statement-sets/${setId}`)); }
    catch(e){
      if(my === seq) state.value = { status: 'error', setId,
        error: (e && e.data && e.data.error) || t('coop.review_failed', 'Could not load this statement’s review.') };
      return;
    }
    if(my !== seq) return;
    state.value = { status: 'ready', setId, diff: diff || {}, allDocs: !!(keep && keep.allDocs), ...kept, ...also };
    if(typeof onChecked === 'function') onChecked(diff || {});
    // the tie-out never waits on the opening, the difference or the documents: each block fills in when its read is in
    await Promise.all([
      readOpening(setId, (diff || {}).group_id).then(opening => { if(opening !== undefined && my === seq) patch({ opening }); }),
      readOpening(setId, (diff || {}).group_id, 'funds')
        .then(fundsOpening => { if(fundsOpening !== undefined && my === seq) patch({ fundsOpening }); }),
      readDifference(setId, diff),
      readSupplierDocs(setId),
    ]);
  }
  function close(){ seq++; state.value = null; stmt.close(); }
  const showAll = () => { const s = state.value; if(s) state.value = { ...s, allDocs: true }; };
  const patch = v => { const s = state.value; if(s) state.value = { ...s, ...v }; };
  const patchSet = (setId, v) => { const s = state.value; if(s && s.setId === setId) state.value = { ...s, ...v }; };

  // book the difference to the account chosen (POST), or undo the booking (DELETE); the statement is checked again
  // after either — booked, it ties and its block says where the difference went; undone, it is held again
  async function bookDifference(){
    const s = state.value;
    if(!s || s.diffBusy) return;
    const id = diffPick.get();
    if(!id){ patch({ diffErr: t('coop.diff_choose', 'Choose the account the difference belongs to.'), diffMsg: '' }); return; }
    patch({ diffBusy: true, diffErr: '', diffMsg: '' });
    let err = '';
    try { await api.post(P(`/statement-sets/${s.setId}/difference`), { account_id: Number(id) }); diffPick.set(''); }
    catch(e){ err = (e && e.data && e.data.error) || t('coop.diff_book_failed', 'Could not book the difference.'); }
    await open(s.setId);
    patchSet(s.setId, { diffBusy: false, diffErr: err });
  }
  async function undoDifference(){
    const s = state.value;
    if(!s || s.diffBusy) return;
    patch({ diffBusy: true, diffErr: '', diffMsg: '', confirmDiffUndo: false });
    let err = '', msg = '';
    try {
      await api.del(P(`/statement-sets/${s.setId}/difference`));
      msg = t('coop.diff_undone', 'The booking is undone: the statement is held until the difference is booked again.');
    }
    catch(e){ err = (e && e.data && e.data.error) || t('coop.diff_undo_failed', 'Could not undo the booking.'); }
    await open(s.setId);
    patchSet(s.setId, { diffBusy: false, diffErr: err, diffMsg: msg });
  }

  // read the held statement again from its PDF (POST …/read-again): the page follows the read (onReadAgain, with the
  // statement as the review's head names it); a refusal says the API's own words
  async function readAgain(){
    const s = state.value;
    if(!s || s.raBusy || s.status === 'loading') return;
    const diff = s.diff || {}, ref = (diff.header || {}).client_ref;
    patch({ raBusy: true, raErr: '' });
    try {
      const r = await api.post(P(`/statement-sets/${s.setId}/read-again`), {});
      patchSet(s.setId, { raBusy: false, raJob: r.job_id });
      if(typeof onReadAgain === 'function') onReadAgain(r.job_id, s.setId, coopName(diff) + (ref ? ' ' + ref : ''));
    } catch(e){
      patchSet(s.setId, { raBusy: false, raErr: (e && e.data && e.data.error) || t('coop.ra_failed', 'Could not read it again.') });
    }
  }
  // the read again the page followed ended without taking the held read's place (it failed, was cancelled, or the page
  // lost touch with it): the review, open on the statement, checks it again and says why under its button (nothing,
  // when the page lost touch: its message says so)
  function readAgainEnded(setId, why){
    const s = state.value;
    if(s && s.setId === setId) open(setId, { raErr: why || '' });
  }

  // move the balance the books hold into the co-op (POST), or undo the move (DELETE) — of a part (PARTS: the farmer's
  // own funds are moved in apart, `part: 'funds'`); its opening is read again after. A move needs no word of its own:
  // the block then says what was moved, from where and when.
  async function moveIn(part = 'coop'){
    const k = PARTS[part], s = state.value, gid = s && s.diff && s.diff.group_id;
    if(!gid || s[k.busy]) return;
    const [kind, id] = picks[part].split(':');
    if(!kind || !id){ patch({ [k.err]: t('coop.opening_choose', 'Choose where your books hold it.'), [k.msg]: '' }); return; }
    patch({ [k.busy]: true, [k.err]: '', [k.msg]: '' });
    let err = '';
    try {
      await api.post(P(`/coops/${gid}/opening`), part === 'funds' ? { kind, id: Number(id), part } : { kind, id: Number(id) });
      picks[part] = '';
    }
    catch(e){ err = (e && e.data && e.data.error) || t('coop.opening_move_failed', 'Could not move the balance in.'); }
    const opening = await readOpening(s.setId, gid, part);
    patch({ [k.busy]: false, [k.err]: err, ...(opening ? { [k.opening]: opening } : {}) });
  }
  async function undoMove(part = 'coop'){
    const k = PARTS[part], s = state.value, gid = s && s.diff && s.diff.group_id;
    if(!gid || s[k.busy]) return;
    const was = (s[k.opening] && s[k.opening].moved) || {};
    patch({ [k.busy]: true, [k.err]: '', [k.msg]: '', [k.confirm]: false });
    let err = '', msg = '';
    try {
      await api.del(P(`/coops/${gid}/opening${k.q}`));
      msg = t('coop.opening_undone', 'The move is undone: {place} holds its balance again.').replace('{place}', was.name || '');
    }
    catch(e){ err = (e && e.data && e.data.error) || t('coop.opening_undo_failed', 'Could not undo the move.'); }
    const opening = await readOpening(s.setId, gid, part);
    patch({ [k.busy]: false, [k.err]: err, [k.msg]: msg, ...(opening ? { [k.opening]: opening } : {}) });
  }

  const coopName = diff => (diff.provider || '').toUpperCase() || t('coop.coop', 'Supplier');
  const sym = () => baseCurrencySymbol();
  // the API's −0.0 (a credit's zero VAT) shows as 0.00, never −0.00
  const money = v => (v === null || v === undefined) ? '—' : fmtMoney(Number(v) || 0, sym(), true);
  const day = iso => formatDate(iso, typeof dateFormat === 'function' ? dateFormat() : dateFormat);
  const tick = ok => html`<span class=${'coop-tie ' + (ok ? 'coop-tie-ok' : 'coop-tie-off')}>${ok ? '✓' : '✗'}</span>`;

  function pageLink(diff, d){
    const at = pageIndex(d.page);
    if(at === null || !diff.extraction_job_id) return html`<span class="muted">—</span>`;
    return html`<button type="button" class="coop-page-link" title=${t('coop.open_page', 'Open the statement on this page')}
      @click=${() => stmt.openPage({ job: diff.extraction_job_id, page: at, title: `${d.ref} · p${d.page}` })}>p${d.page}</button>`;
  }

  // the co-op's first statement: its opening, and where the books may already hold it — moved in once, or the move made.
  // The co-op by its own name (the provider's, before the API says it). Per part (PARTS): what the farmer owes the co-op
  // ('coop'), and on the first statement that shows them the farmer's own funds at it ('funds', 2026-10-27, plan
  // §12.5) — the farmer's money, an asset the books may keep elsewhere (an investment, a loan to the co-op), moved in
  // apart: never netted against what is owed.
  function openingBlock(s, diff, provider, part = 'coop'){
    const k = PARTS[part], o = s[k.opening], funds = part === 'funds';
    if(!o || !o.first_set_id || o.first_set_id !== s.setId) return [];
    const coop = o.name || provider;
    const place = c => c.kind === 'account' ? `${c.code} ${c.name}` : c.name;
    const busy = s[k.busy];
    const notes = html`${s[k.err] ? html`<div class="coop-opening-err">⚠ ${s[k.err]}</div>` : []}
      ${s[k.msg] ? html`<div class="coop-opening-msg">✓ ${s[k.msg]}</div>` : []}`;
    const title = funds ? t('coop.funds_opening_title', 'Opening balance — your own funds at {coop}').replace('{coop}', coop)
      : t('coop.opening_title', 'Opening balance');
    const wrap = 'coop-opening' + (funds ? ' coop-opening-funds' : '');
    const m = o.moved;
    if(m){
      return html`<div class=${wrap}>
        <h3 class="coop-h">${title}</h3>
        <div>${(funds
          ? t('coop.funds_opening_moved', '{amount} that your books held at {place} before {date} was moved into your own funds at {coop}: they are in the books once, in their cashbooks.')
          : t('coop.opening_moved', '{amount} that your books held at {place} before {date} was moved into {coop}: the balance is in the books once, in its cashbooks.'))
          .replace('{amount}', money(m.amount)).replace('{place}', m.name || '').replace('{date}', day(m.date)).replace('{coop}', coop)}</div>
        ${m.stale ? html`<div class="coop-opening-stale">⚠ ${t('coop.opening_stale', '{coop} now starts on {start}: an older statement was imported since. Undo the move and move the balance in again.')
          .replace('{coop}', coop).replace('{start}', day(o.start))}</div>` : []}
        <div class="coop-opening-row coop-opening-end">
          ${s[k.confirm]
            ? html`<span class="confirm-inline"><span class="confirm-q">${t('coop.opening_undo_q', 'Undo the move?')}</span>
                <button type="button" class="btn btn-sm btn-danger coop-open-undo-yes" ?disabled=${busy} @click=${() => undoMove(part)}>${t('common.yes', 'Yes')}</button>
                <button type="button" class="btn btn-sm btn-outline coop-open-undo-no" @click=${() => patch({ [k.confirm]: false })}>${t('common.no', 'No')}</button></span>`
            : html`<button type="button" class="btn btn-sm btn-outline coop-open-undo" ?disabled=${busy}
                @click=${() => patch({ [k.confirm]: true, [k.err]: '', [k.msg]: '' })}>${busy ? t('coop.opening_undoing', 'Undoing…') : t('coop.opening_undo', 'Undo the move')}</button>`}
        </div>
        ${notes}
      </div>`;
    }
    const cands = o.candidates || [];
    return html`<div class=${wrap}>
      <h3 class="coop-h">${title}</h3>
      <div>${(funds
        ? t('coop.funds_opening_is', 'Your own funds at {coop} open at {amount} on {date}: they are posted into their cashbooks against Opening Balance Equity.')
        : t('coop.opening_is', 'The statement opens at {amount} on {date}: it is posted into the supplier’s cashbooks against Opening Balance Equity.'))
        .replace('{coop}', coop).replace('{amount}', money(o.opening)).replace('{date}', day(o.start))}</div>
      ${cands.length ? html`
        <div>${(funds
          ? t('coop.funds_opening_ask', 'If your books already hold these funds (an investment, a loan to {coop}, or an account you kept for them), move them in, so they are in the books once.')
          : t('coop.opening_ask', 'If your books already hold this balance (on {coop} as a supplier, or in an account you kept for it), move it in, so it is in the books once.'))
          .replace('{coop}', coop)}</div>
        <div class="coop-opening-row">
          <select class="form-control cb-in coop-open-pick" aria-label=${t('coop.opening_where', 'Where your books hold it')}
            .value=${picks[part]} @change=${e => { picks[part] = e.target.value; }}>
            <option value="">${t('coop.opening_pick_opt', '— Where does it sit? —')}</option>
            ${cands.map(c => html`<option value=${c.kind + ':' + c.id}>${place(c)} · ${money(c.balance)}</option>`)}
          </select>
          <button type="button" class="btn btn-sm btn-primary coop-open-go" ?disabled=${busy || !diff.cleared} @click=${() => moveIn(part)}>
            ${busy ? t('coop.opening_moving', 'Moving…') : t('coop.opening_move', 'Move it in')}</button>
        </div>
        ${!diff.cleared ? html`<div class="coop-note muted">${t('coop.opening_wait', 'It can be moved in once the statement ties to its PDF.')}</div>` : []}`
        : html`<div class="coop-note muted">${(funds
          ? t('coop.funds_opening_none', 'Nothing in your books before {date} holds these funds to move in.')
          : t('coop.opening_none', 'Nothing in your books before {date} holds a balance to move in.')).replace('{date}', day(o.start))}</div>`}
      ${notes}
    </div>`;
  }

  // the difference no move explains (s.difference, GET …/difference): where it may be booked, or why not yet; where it
  // is booked, with its undo; or one booked that the statements no longer call for, to undo. Nothing until it is read.
  function differenceBlock(s, diff){
    const d = s.difference, c = diff.continuity || {};
    if(!d) return [];
    const notes = html`${s.diffErr ? html`<div class="coop-diff-err">⚠ ${s.diffErr}</div>` : []}
      ${s.diffMsg ? html`<div class="coop-diff-msg">✓ ${s.diffMsg}</div>` : []}`;
    const undo = html`<div class="coop-diff-row coop-diff-end">
      ${s.confirmDiffUndo
        ? html`<span class="confirm-inline"><span class="confirm-q">${t('coop.diff_undo_q', 'Undo the booking?')}</span>
            <button type="button" class="btn btn-sm btn-danger coop-diff-undo-yes" ?disabled=${s.diffBusy} @click=${undoDifference}>${t('common.yes', 'Yes')}</button>
            <button type="button" class="btn btn-sm btn-outline coop-diff-undo-no" @click=${() => patch({ confirmDiffUndo: false })}>${t('common.no', 'No')}</button></span>`
        : html`<button type="button" class="btn btn-sm btn-outline coop-diff-undo" ?disabled=${s.diffBusy}
            @click=${() => patch({ confirmDiffUndo: true, diffErr: '', diffMsg: '' })}>${s.diffBusy ? t('coop.diff_undoing', 'Undoing…') : t('coop.diff_undo', 'Undo the booking')}</button>`}
    </div>`;
    if(d.booked){
      const b = d.booked, a = b.account || {};
      return html`<div class="coop-diff">
        <div class="coop-carry-ok">✓ ${t('coop.diff_booked', 'The difference of {amount} is booked to {account} on {date}: the cashbooks open as the statement prints them.')
          .replace('{amount}', money(b.amount)).replace('{account}', `${a.code || ''} ${a.name || ''}`.trim()).replace('{date}', day(b.date))}</div>
        ${undo}${notes}
      </div>`;
    }
    if(d.undo){
      return html`<div class="coop-diff">
        <div class="coop-carry-off">⚠ ${t('coop.diff_stale', 'The difference booked for this statement is no longer what the statements say — undo it, and book it again if it is still needed.')}</div>
        ${undo}${notes}
      </div>`;
    }
    if(c.verdict !== 'unexplained') return [];
    return html`<div class="coop-diff">
      <div>${t('coop.diff_ask', 'If the statement is right — the supplier paid part of it from your own funds with them that this statement does not show, charged or credited something with no line on it, or a sub-account first shows a balance it carried before — book the difference ({amount}) to the account it belongs to: the cashbooks then open as the statement prints them, and it can post. Where no account of yours keeps those funds, Opening Balance Equity takes it.')
        .replace('{amount}', money(d.amount))}</div>
      ${d.can_book
        ? html`<div class="coop-diff-row">${diffPick.el}
            <button type="button" class="btn btn-sm btn-primary coop-diff-go" ?disabled=${s.diffBusy} @click=${bookDifference}>
              ${s.diffBusy ? t('coop.diff_booking', 'Booking…') : t('coop.diff_book', 'Book the difference')}</button></div>`
        : html`<div class="coop-note muted coop-diff-why">${d.why || ''}</div>`}
      ${notes}
    </div>`;
  }

  // a later statement against the co-op's statement before it (diff.continuity): each sub-account's closing there and
  // opening here, in the cashbook's sign — and, for one new on this statement, what was paid into it before then
  // (paid_in: a payment from another cashbook booked to it, which its opening carries). The statement before opens its
  // own review. Nothing on the co-op's first statement (its opening block says what it opens at).
  function carryBlock(s, diff){
    const c = diff.continuity, p = c && c.previous;
    if(!c || !p || !['continues', 'moved', 'gap', 'unexplained', 'explained'].includes(c.verdict)) return [];
    const paid = (c.rows || []).some(r => Math.abs(Number(r.paid_in || 0)) >= 0.005);
    const before = html`<div class="coop-carry-prev">${t('coop.carry_before', 'The statement before:')}
      <button type="button" class="coop-page-link coop-carry-open" title=${t('coop.carry_open', 'Open its review')}
        @click=${() => open(p.statement_set_id)}>${day(p.period_from)} – ${day(p.period_to)}</button>
      ${p.status === 'held' ? html`<span class="coop-pill coop-pill-held">⚠ ${t('coop.status_held', 'Held')}</span>` : []}</div>`;
    let say, mark = 'coop-row-off';
    if(c.verdict === 'continues'){
      say = html`<div class="coop-carry-ok">✓ ${paid
        ? t('coop.carry_continues_paid', 'Each sub-account opens where the statement before closed it — one new on this statement with what was paid into it before then.')
        : t('coop.carry_continues', 'Each sub-account opens where the statement before closed it.')}</div>`;
    } else if(c.verdict === 'moved'){
      mark = 'coop-row-moved';
      const status = c.booked
        ? html`<div class="coop-carry-ok">✓ ${t('coop.carry_booked', 'Booked on {date}: one entry moves each cashbook by its difference — what you owe the supplier in all is unchanged.')
            .replace('{date}', day(c.booked.date))}</div>`
        : c.locked
          ? html`<div class="coop-carry-off">⚠ ${t('coop.carry_locked', 'Not booked: {date} falls in a locked financial year.').replace('{date}', day(c.date))}</div>`
          : html`<div class="coop-carry-wait">${p.status === 'held'
              ? t('coop.carry_wait_before', 'Not booked yet: it is booked once the statement before ties to its PDF.')
              : diff.status === 'held'
                ? t('coop.carry_wait_this', 'Not booked yet: it is booked once this statement ties to its PDF.')
                : t('coop.carry_wait_next', 'Not booked yet: it is booked when a statement of the supplier is next imported or a line of it posted.')}</div>`;
      say = html`<div>${t('coop.carry_moved', 'The supplier moved balances between its sub-accounts on {date}: the statement before closed them so and this one opens them so — no line shows the move.')
        .replace('{date}', day(c.date))}</div>${status}`;
    } else if(c.verdict === 'gap'){
      say = html`<div class="coop-carry-off">⚠ ${t('coop.carry_gap', 'A statement is missing: the statement before ends on {end} and this one starts on {start}.')
        .replace('{end}', day(p.period_to)).replace('{start}', day(c.date))}</div>`;
    } else if(c.verdict === 'explained'){
      mark = 'coop-row-moved';
      say = html`<div>${t('coop.carry_explained', 'Its sub-accounts do not open where the statement before closed them, and no move between them explains it — the difference is booked to the account it belongs to.')}</div>`;
    } else {
      say = html`<div class="coop-carry-off">⚠ ${t('coop.carry_unexplained', 'Its sub-accounts do not open where the statement before closed them, and no move between them explains it.')}</div>`;
    }
    const rows = c.verdict === 'continues' && !paid ? [] : (c.rows || []);
    const off = r => r.difference === null || r.difference === undefined || Math.abs(Number(r.difference)) >= 0.005;
    return html`<div class="coop-carry">
      <h3 class="coop-h">${t('coop.carry_title', 'From the statement before')}</h3>
      ${before}
      ${say}
      ${rows.length ? html`<div class="table-wrap"><table class="cb-table coop-table coop-carry-table">
        <thead><tr><th>${t('coop.section', 'Sub-account')}</th><th class="text-right">${t('coop.carry_closed', 'Closed before')}</th>
          ${paid ? html`<th class="text-right">${t('coop.carry_paid_in', 'Paid in before')}</th>` : []}
          <th class="text-right">${t('coop.carry_opens', 'Opens here')}</th><th class="text-right">${t('coop.difference', 'Difference')}</th></tr></thead>
        <tbody>${rows.map(r => html`<tr class=${off(r) ? mark : ''}>
          <td><span class="mono">${r.section}</span> ${r.cashbook}</td>
          <td class="text-right mono">${money(r.closed)}</td>
          ${paid ? html`<td class="text-right mono">${money(r.paid_in)}</td>` : []}
          <td class="text-right mono">${money(r.opens)}</td>
          <td class="text-right mono">${money(r.difference)}</td></tr>`)}</tbody>
      </table></div>` : []}
      ${differenceBlock(s, diff)}
    </div>`;
  }

  // the purchase documents on the co-op as a supplier dated in the statement's period (s.supplierDocs): each opens in its
  // editor, its total in its own currency. Nothing when there are none, or until they are read.
  function supplierDocsBlock(s, provider){
    const d = s.supplierDocs, docs = (d && d.documents) || [];
    if(!docs.length) return [];
    const coop = d.coop || provider;
    const noun = code => { const cfg = DOC_TYPES[KEY_FOR_TYPE[code]] || {};
      return cfg.nounKey ? t(cfg.nounKey, cfg.noun || code) : (cfg.noun || code); };
    const openDoc = x => { close(); navigate('/co/' + companyId + '/' + KEY_FOR_TYPE[x.type_code] + '/' + x.id + '/edit'); };
    return html`<div class="coop-supplier-docs">
      <h3 class="coop-h">${t('coop.supplier_docs_title', 'Also on {coop} as a supplier').replace('{coop}', coop)}</h3>
      <div class="coop-carry-off">⚠ ${t('coop.supplier_docs_note', 'These documents are on {coop} as a supplier and dated in this statement’s period. One whose purchase is on this statement is in the books twice — reverse it (a draft: do not confirm it).')
        .replace('{coop}', coop)}</div>
      <div class="table-wrap"><table class="cb-table coop-table coop-supplier-docs-table">
        <thead><tr><th>${t('coop.document', 'Document')}</th><th>${t('common.date', 'Date')}</th>
          <th class="text-right">${t('doc.total', 'Total')}</th><th>${t('common.status', 'Status')}</th></tr></thead>
        <tbody>${docs.map(x => html`<tr>
          <td>${KEY_FOR_TYPE[x.type_code]
            ? html`<button type="button" class="coop-cb-link coop-doc-open" @click=${() => openDoc(x)}>${noun(x.type_code)} ${x.document_no}</button>`
            : `${noun(x.type_code)} ${x.document_no}`}</td>
          <td class="mono">${day(x.document_date)}</td>
          <td class="text-right mono">${fmtMoney(Number(x.total) || 0, x.currency_symbol || x.currency_code || sym(), true)}</td>
          <td><span class=${statusClass(x.status)}>${statusLabel(x.status)}</span></td></tr>`)}</tbody>
      </table></div>
    </div>`;
  }

  // a held statement read again from its PDF (diff.read_again: whether it can be, or why not): what it does and its
  // button — which waits while the statement is checked again (a check holds nothing of a read it did not see start); while
  // it reads, that it reads. Nothing for a statement that is not held.
  function readAgainBlock(s, diff){
    const ra = diff.read_again;
    if(diff.status !== 'held' || !ra) return [];
    if(s.raJob) return html`<div class="coop-ra"><div class="coop-carry-wait">⏳ ${t('coop.ra_reading', 'Reading the PDF again — the review opens again when it is done.')}</div></div>`;
    if(!ra.can) return html`<div class="coop-ra"><div class="coop-note muted coop-ra-why">${ra.why || ''}</div></div>`;
    return html`<div class="coop-ra">
      <div>${t('coop.ra_ask', 'If the AI misread the PDF, read it again: the new read replaces this one and is checked against the PDF like any import.')}</div>
      <div class="coop-ra-row">
        <button type="button" class="btn btn-sm btn-primary coop-ra-go" ?disabled=${s.raBusy || s.status === 'loading'} @click=${readAgain}>
          ${s.raBusy ? t('coop.ra_starting', 'Starting…') : t('coop.ra_go', 'Read again')}</button>
      </div>
      ${s.raErr ? html`<div class="coop-ra-err">⚠ ${s.raErr}</div>` : []}
    </div>`;
  }

  // a held statement's line at the top: what holds it — its PDF, the statement before, a locked year (heldWhy). Held by
  // its PDF alone, it says so as it always has.
  function heldNote(diff){
    const why = heldWhy(diff);
    if(!why.carry && !why.lock && !why.stale) return t('coop.held_note', 'This statement does not tie to its PDF yet — nothing of it posts until it does.');
    return t('coop.held_why', 'This statement is held: {why} — nothing of it posts until it is put right.')
      .replace('{why}', heldReasons(why, t).join('; '));
  }

  function panel(s){
    const diff = s.diff || {};
    const st = setStatus(diff.status);
    const head = diff.header || {};
    const period = [head.period_start, head.period_end].filter(Boolean).map(day).join(' – ');
    const entries = diff.entries || [], skipped = diff.skipped || [], cashbooks = diff.cashbooks || [];
    const docs = reviewDocuments(diff.documents || []);
    const docsOff = docs.filter(d => !d.ties).length;
    const shownDocs = s.allDocs ? docs : docs.slice(0, Math.max(DOCS_SHOWN, docsOff));
    const vat = diff.vat || [], problems = diff.problems || [];
    const cashDocs = Object.keys(diff.cash || {}).length;
    const name = coopName(diff);
    const body = s.status === 'loading' && !s.diff
      ? html`<div class="cb-stmt-locating">${t('coop.checking', 'Checking the statement against its PDF…')}</div>`
      : s.status === 'error'
        ? html`<div class="muted-block cb-stmt-err">${s.error}</div>`
        : html`
          <div class=${'coop-verdict coop-verdict-' + st.tone}>
            ${diff.status === 'held'
              ? heldNote(diff)
              : diff.status === 'posted'
                ? t('coop.posted_note', 'It ties to its PDF and every line of it is posted.')
                : t('coop.ties_note', 'It ties to its PDF — its lines post as they are allocated.')}
          </div>
          ${problems.length ? html`<ul class="coop-problems">${problems.map(p => html`<li>⚠ ${p}</li>`)}</ul>` : []}
          ${readAgainBlock(s, diff)}
          ${openingBlock(s, diff, name)}
          ${openingBlock(s, diff, name, 'funds')}
          ${supplierDocsBlock(s, name)}

          <h3 class="coop-h">${t('coop.where_it_went', 'Where it went')}</h3>
          <div class="table-wrap"><table class="cb-table coop-table">
            <thead><tr><th>${t('coop.section', 'Sub-account')}</th><th>${t('coop.cashbook', 'Cashbook')}</th>
              <th class="text-right">${t('coop.lines', 'Lines')}</th></tr></thead>
            <tbody>
              ${entries.map(e => html`<tr>
                <td><span class="mono">${e.section}</span> ${e.label || ''}</td>
                <td>${typeof onOpenCashbook === 'function' && e.cashbook_id
                    ? html`<button type="button" class="coop-cb-link" @click=${() => { close(); onOpenCashbook(e.cashbook_id); }}>${e.label || e.section}</button>`
                    : (e.label || e.section)}
                  <span class=${'coop-made ' + (e.created ? 'coop-made-new' : '')}>${e.created ? t('coop.made_now', 'new') : t('coop.matched', 'matched')}</span></td>
                <td class="text-right mono">${e.staged || 0}${e.duplicates ? html` <span class="muted" title=${t('coop.dupes_hint', 'Lines already in the books from an earlier import')}>+${e.duplicates} ${t('coop.dupes', 'dup.')}</span>` : []}</td>
              </tr>`)}
              ${skipped.map(k => html`<tr class="coop-skipped">
                <td><span class="mono">${k.section}</span> ${k.label || ''}</td>
                <td colspan="2" class="muted">${t('coop.not_imported', 'Not imported')} — ${k.reason}${k.line_count ? ' (' + (k.line_count === 1
                  ? t('coop.one_line', '1 line') : t('coop.n_lines', '{n} lines').replace('{n}', k.line_count)) + ')' : ''}</td>
              </tr>`)}
              ${!entries.length && !skipped.length ? html`<tr><td colspan="3" class="muted">${t('coop.no_fanout', 'This import kept no record of where it routed the statement.')}</td></tr>` : []}
            </tbody>
          </table></div>
          ${diff.orphan_lines ? html`<div class="coop-note">⚠ ${t('coop.orphans', '{n} line(s) on the statement name no sub-account and were not imported').replace('{n}', diff.orphan_lines)}</div>` : []}

          <h3 class="coop-h">${t('coop.cashbooks_tie', 'Each cashbook against the statement')}</h3>
          <div class="table-wrap"><table class="cb-table coop-table">
            <thead><tr><th>${t('coop.cashbook', 'Cashbook')}</th><th class="text-right">${t('coop.opening', 'Opening')}</th>
              <th class="text-right">${t('coop.plus_lines', '+ Lines')}</th><th class="text-right">${t('coop.printed_closing', 'Printed closing')}</th>
              <th class="text-right">${t('coop.difference', 'Difference')}</th><th></th></tr></thead>
            <tbody>${cashbooks.map(c => html`<tr class=${c.ties ? '' : 'coop-row-off'}>
              <td><span class="mono">${c.section}</span> ${c.cashbook}</td>
              <td class="text-right mono">${money(c.opening)}</td>
              <td class="text-right mono">${money(c.lines_total)}</td>
              <td class="text-right mono">${money(c.closing)}</td>
              <td class="text-right mono">${c.difference === null || c.difference === undefined ? (c.why || '—') : money(c.difference)}</td>
              <td>${tick(c.ties)}</td></tr>`)}</tbody>
          </table></div>
          ${carryBlock(s, diff)}
          <div class="coop-note muted">${t('coop.sign_note', 'As the cashbook keeps them: what you owe the supplier is negative.')}</div>

          <h3 class="coop-h">${t('coop.documents_tie', 'Documents')} <span class="muted">${docs.length}${docsOff ? ' · ' + t('coop.n_off', '{n} do not tie').replace('{n}', docsOff) : ''}</span></h3>
          ${docs.length ? html`<div class="table-wrap"><table class="cb-table coop-table">
            <thead><tr><th>${t('coop.document', 'Document')}</th><th>${t('common.date', 'Date')}</th><th>${t('coop.page', 'Page')}</th>
              <th class="text-right">${t('coop.printed', 'Printed')}</th><th class="text-right">${t('coop.imported', 'Imported')}</th>
              <th class="text-right">${t('coop.printed_vat', 'Printed VAT')}</th><th class="text-right">${t('coop.imported_vat', 'Imported VAT')}</th><th></th></tr></thead>
            <tbody>${shownDocs.map(d => html`<tr class=${d.ties ? '' : 'coop-row-off'}>
              <td class="mono">${d.ref}</td><td class="mono">${day(d.date)}</td><td>${pageLink(diff, d)}</td>
              <td class="text-right mono">${money(d.printed_total)}</td><td class="text-right mono">${money(d.staged_total)}</td>
              <td class="text-right mono">${money(d.printed_vat)}</td><td class="text-right mono">${money(d.staged_vat)}</td>
              <td>${tick(d.ties)}</td></tr>`)}</tbody>
          </table></div>
          ${shownDocs.length < docs.length ? html`<button type="button" class="btn btn-sm btn-outline coop-show-all" @click=${showAll}>${t('coop.show_all_docs', 'Show all {n} documents').replace('{n}', docs.length)}</button>` : []}`
          : html`<div class="coop-note muted">${t('coop.no_docs', 'No document of it is compared in full (its lines sit in sub-accounts that are not imported).')}</div>`}

          ${vat.length || diff.diesel || diff.account_tie ? html`<h3 class="coop-h">${t('coop.vat_tie', 'VAT and totals')}</h3>
          <div class="table-wrap"><table class="cb-table coop-table">
            <thead><tr><th></th><th class="text-right">${t('coop.printed', 'Printed')}</th><th class="text-right">${t('coop.imported', 'Imported')}</th>
              <th class="text-right">${t('coop.printed_vat', 'Printed VAT')}</th><th class="text-right">${t('coop.imported_vat', 'Imported VAT')}</th><th></th></tr></thead>
            <tbody>
              ${vat.map(v => html`<tr class=${v.ties ? '' : 'coop-row-off'}>
                <td>${v.section ? html`<span class="mono">${v.section}</span> ${day(v.statement_date)}` : t('coop.basis_' + v.basis, BASIS[v.basis] || v.basis)}</td>
                <td class="text-right mono">${v.printed_incl === null ? '' : money(v.printed_incl)}</td>
                <td class="text-right mono">${v.staged_incl === null ? '' : money(v.staged_incl)}</td>
                <td class="text-right mono">${money(v.printed_vat)}</td><td class="text-right mono">${money(v.staged_vat)}</td>
                <td>${tick(v.ties)}</td></tr>`)}
              ${diff.diesel ? html`<tr class=${diff.diesel.ties ? '' : 'coop-row-off'}>
                <td>${t('coop.diesel_litres', 'Diesel litres')}</td>
                <td class="text-right mono">${Number(diff.diesel.printed).toFixed(2)}</td><td class="text-right mono">${Number(diff.diesel.lines).toFixed(2)}</td>
                <td></td><td></td><td>${tick(diff.diesel.ties)}</td></tr>` : []}
              ${diff.account_tie ? html`<tr class=${diff.account_tie.ties ? '' : 'coop-row-off'}>
                <td>${t('coop.account_opening', 'Account opening')}</td>
                <td class="text-right mono">${money(diff.account_tie.printed_opening)}</td><td class="text-right mono">${money(diff.account_tie.sections_opening)}</td>
                <td></td><td></td><td>${tick(diff.account_tie.ties)}</td></tr>` : []}
            </tbody>
          </table></div>` : []}
          ${cashDocs ? html`<div class="coop-note muted">${t('coop.cash_note', '{n} cash sale(s) at the till are not on the account — they count in the VAT analysis only.').replace('{n}', cashDocs)}</div>` : []}`;
    return html`<div class="cb-stmt-overlay coop-overlay" @click=${e => { if(e.target === e.currentTarget) close(); }}>
      <div class="cb-stmt-modal coop-review" role="dialog" aria-label=${t('coop.review', 'Supplier statement review')}>
        <div class="cb-stmt-head">
          <span class="fw-700 cb-stmt-title">${name}${head.client_ref ? ' ' + head.client_ref : ''}${period ? ' · ' + period : ''}</span>
          ${diff.status ? html`<span class=${'coop-pill coop-pill-' + st.tone}>${st.tone === 'held' ? '⚠ ' : (st.tone === 'ok' ? '✓ ' : '')}${t(st.key, st.text)}</span>` : []}
          <span class="cb-mode-spacer"></span>
          <button type="button" class="btn btn-sm btn-outline coop-recheck" ?disabled=${s.status === 'loading'} @click=${() => open(s.setId)}>${s.status === 'loading' ? t('coop.checking_short', 'Checking…') : t('coop.check_again', 'Check again')}</button>
          <button type="button" class="cb-clear-btn" title=${t('common.close', 'Close')} @click=${close}>✕</button>
        </div>
        <div class="cb-stmt-body coop-body">${body}</div>
      </div>
    </div>`;
  }

  // the review, with the statement's page on top of it when one is open. It keeps its place: every change (a page
  // opened on top and closed, "Show all", "Check again") draws the panel anew, which would put a long review back at
  // its top. The new panel is in place once this draw is done, so the old one's scroll is put back on it then.
  let drawnSet = null;
  const view = () => {
    const s = state.value, page = stmt.state.value;
    const was = s && s.setId === drawnSet ? document.querySelector('.coop-body') : null;
    const top = was ? was.scrollTop : 0;
    drawnSet = s ? s.setId : null;
    if(top) queueMicrotask(() => { const b = document.querySelector('.coop-body'); if(b) b.scrollTop = top; });
    return html`${s ? panel(s) : []}${page ? stmt.view() : []}`;
  };
  return { open, close, state, view, readAgainEnded };
}
